import { Outlet, NavLink, useLocation } from "react-router-dom";
import {
  FiGrid, FiUser, FiMenu, FiX,
  FiChevronsLeft, FiChevronsRight, FiMapPin, FiChevronDown,
  FiChevronUp, FiChevronRight, FiCreditCard, FiShoppingBag,
  FiAward, FiPercent, FiBox, FiShoppingCart, FiBookOpen
} from "react-icons/fi";
import { FaCoins } from "react-icons/fa";
import { RiPaletteLine } from "react-icons/ri";
import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import type { RootState, AppDispatch } from "../redux/store";
import PlanSelectionModal from "../components/PlanSelectionModal";
import SubscriptionDetailsModal from "../components/SubscriptionDetailsModal";
import VersionBadge from "../components/changelog/VersionBadge";
import ReleaseNotesModal from "../components/changelog/ReleaseNotesModal";
import EditProfileModal from "../components/EditProfileModal";
import { fetchDealerProfile } from "../redux/dealerSlice";
import { getStoreCredit } from "../api/api";

export default function DealerLayout() {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { profile, previewTier } = useSelector((state: RootState) => state.dealer);

  useEffect(() => {
    if (!profile) {
      dispatch(fetchDealerProfile());
    }
  }, [dispatch, profile]);

  const [storeCreditBalance, setStoreCreditBalance] = useState<string | null>(null);
  const [storeCreditAmount, setStoreCreditAmount] = useState<number>(0);
  const [isReleaseNotesOpen, setIsReleaseNotesOpen] = useState(false);
  const sidebarRef = useRef<HTMLElement>(null);
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const fetchCredits = async () => {
      try {
        const response = await getStoreCredit();
        if (response.data) {
          const rawData = response.data as any;
          const storeCredit = rawData.data !== undefined ? rawData.data : rawData;

          const shopsArray = Array.isArray(storeCredit)
            ? storeCredit
            : Array.isArray(storeCredit.data)
              ? storeCredit.data
              : [];

          let total = 0;
          let currency = 'USD';

          shopsArray.forEach((shopItem: any) => {
            if (Array.isArray(shopItem.credits)) {
              shopItem.credits.forEach((creditItem: any) => {
                const amt = typeof creditItem.amount === 'number' ? creditItem.amount : parseFloat(creditItem.amount) || 0;
                total += amt;
                if (creditItem.currencyCode) {
                  currency = creditItem.currencyCode;
                }
              });
            }
          });

          const symbol = currency === 'CAD' ? 'CA$' : '$';
          setStoreCreditAmount(total);
          setStoreCreditBalance(`${symbol}${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
        }
      } catch (err) {
        console.error("Failed to fetch credits for sidebar", err);
      }
    };
    fetchCredits();
  }, []);

  const visualTierLevel = (() => {
    const rawTier = typeof profile?.tier === 'object' && profile?.tier !== null
      ? (profile.tier as any).name
      : profile?.tier || 'bronze';
    const tierName = String(rawTier).toLowerCase();
    const visualTierName = previewTier || tierName || "bronze";

    const t = visualTierName.toLowerCase();
    if (t === "platinum") return 3;
    if (t === "gold") return 2;
    if (t === "silver") return 1;
    if (t === "bronze") return 0;

    const revenue = profile?.revenueLifetime;
    if (revenue !== undefined && revenue !== null) {
      if (revenue >= 10000) return 3;
      if (revenue >= 6000) return 2;
      if (revenue >= 2500) return 1;
      return 0;
    }
    return 0;
  })();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const location = useLocation();

  const getTierBadge = (level: number) => {
    switch (level) {
      case 3: return "/platinum.png";
      case 2: return "/gold.png";
      case 1: return "/silver.png";
      default: return "/bronze.png";
    }
  };

  const dashboardItem = { name: "Dashboard", path: "/dashboard", icon: <FiGrid /> };

  const myAccountSubItems = [
    { name: "Profile", path: "/profile", icon: <FiUser /> },
    { name: "Orders", path: "/orders", icon: <FiShoppingBag /> },
    { name: "Payment Methods", path: "/saved-payment-methods", icon: <FiCreditCard /> },
  ];

  // const storeListingSubItems = [
  //   { name: "Locator Settings", path: "/locator-settings", icon: <FiSettings /> },
  //   { name: "Leads", path: "/leads", icon: <FiMail /> },
  //   { name: "Services", path: "/services", icon: <FiList /> },
  //   { name: "Gallery", path: "/gallery", icon: <FiImage /> },
  //   { name: "Social Links", path: "/social", icon: <FiShare2 /> },
  // ];

  const separateItems = [
    { name: "Dealer Program", path: "/dealer-program", icon: <FiAward /> },
    { name: "Profit Calculator", path: "/profit-calculator", icon: <FiPercent /> },
    { name: "Sales Resources", path: "/sales-resources", icon: <FiBox /> },
    { name: "Digital Art", path: "/digital-art", icon: <RiPaletteLine /> },
  ];

  const isMyAccountSubActive = myAccountSubItems.some(item => location.pathname === item.path);
  const [isMyAccountOpen, setIsMyAccountOpen] = useState(true);

  const tierLabel = ["Bronze", "Silver", "Gold", "Platinum"][visualTierLevel] || "Bronze";
  const tierTheme = [
    { card: "dealer-tier-card--bronze", label: "text-[#79512B]", tier: "text-[#68421F]", icon: "ring-[#D9B78E]" },
    { card: "dealer-tier-card--silver", label: "text-[#596577]", tier: "text-[#263548]", icon: "ring-[#C3CCD6]" },
    { card: "dealer-tier-card--gold", label: "text-[#765513]", tier: "text-[#5E430C]", icon: "ring-[#E2C46E]" },
    { card: "dealer-tier-card--platinum", label: "text-[#C7D4E6]", tier: "text-white", icon: "ring-[#91A9C9]" },
  ][visualTierLevel] || { card: "dealer-tier-card--bronze", label: "text-[#79512B]", tier: "text-[#68421F]", icon: "ring-[#D9B78E]" };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
    if (window.matchMedia("(max-width: 767px)").matches) {
      window.requestAnimationFrame(() => mobileMenuButtonRef.current?.focus());
    }
  };

  useEffect(() => {
    if (!isSidebarOpen) return;

    const sidebar = sidebarRef.current;
    const getFocusableItems = () => Array.from(sidebar?.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ) ?? []).filter(item => item.getClientRects().length > 0);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setIsSidebarOpen(false);
        window.requestAnimationFrame(() => mobileMenuButtonRef.current?.focus());
        return;
      }

      if (event.key !== "Tab") return;
      const focusableItems = getFocusableItems();
      if (!focusableItems.length) {
        event.preventDefault();
        return;
      }

      const firstItem = focusableItems[0];
      const lastItem = focusableItems[focusableItems.length - 1];
      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault();
        lastItem.focus();
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault();
        firstItem.focus();
      }
    };

    const focusFrame = window.requestAnimationFrame(() => getFocusableItems()[0]?.focus());
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isSidebarOpen]);

  useEffect(() => {
    if (isMyAccountSubActive) {
      setIsMyAccountOpen(true);
    }
  }, [isMyAccountSubActive]);


  // useEffect(() => {
  //   if (isStoreListingSubActive) {
  //     setIsStoreListingOpen(true);
  //   }
  // }, [isStoreListingSubActive]);



  // Dispatch window resize event to force responsive charts to recalculate layout
  useEffect(() => {
    window.dispatchEvent(new Event('resize'));
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 310);
    return () => clearTimeout(timer);
  }, [isCollapsed]);

  const collapsed = isCollapsed && !isSidebarOpen;

  return (
    <div className="flex h-full bg-gray-50 font-poppins overflow-hidden">
      {/* MOBILE OVERLAY */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          aria-hidden="true"
          onClick={closeSidebar}
        />
      )}

      <aside
        ref={sidebarRef}
        id="dealer-sidebar"
        role={isSidebarOpen ? "dialog" : undefined}
        aria-modal={isSidebarOpen ? true : undefined}
        aria-label={isSidebarOpen ? "Dealer portal navigation" : undefined}
        className={`dealer-sidebar-shell fixed inset-y-0 left-0 z-50 flex flex-col shadow-xl border-r transition-all duration-300 ease-in-out
          md:relative md:translate-x-0 
          ${isSidebarOpen ? "visible translate-x-0 w-72" : "invisible -translate-x-full md:visible md:translate-x-0"}
          ${collapsed ? "md:w-20" : "md:w-72"}
        `}
      >
        {/* Sidebar Header */}
        <div className="h-24 flex flex-col items-center justify-center border-b border-gray-100 flex-shrink-0 relative px-3">
          {!collapsed && (
            <NavLink
              to="/dealer-program"
              onClick={closeSidebar}
              aria-label={`View ${tierLabel} tier and dealer program benefits`}
              className={`dealer-tier-card ${tierTheme.card} flex items-center gap-2.5 w-full min-h-[60px] rounded-xl border px-3 py-2 pr-8 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B08D55]`}
              title="View tier benefits"
            >
              <img
                src={getTierBadge(visualTierLevel)}
                alt=""
                aria-hidden="true"
                className={`w-9 h-9 shrink-0 rounded-full object-contain ring-1 ${tierTheme.icon}`}
              />
              <span className="flex min-w-0 flex-1 flex-col items-start leading-tight">
                <span className={`text-[10px] font-bold uppercase tracking-wider ${tierTheme.label}`}>
                  {previewTier ? "Tier preview" : "Your current tier"}
                </span>
                <span className={`text-sm font-extrabold tracking-wide ${tierTheme.tier}`}>
                  {tierLabel}
                </span>
                <span className={`mt-0.5 text-[10px] font-medium ${tierTheme.label}`}>
                  View tier benefits
                </span>
              </span>
              <FiChevronRight aria-hidden="true" className={`shrink-0 ${tierTheme.label}`} size={15} />
            </NavLink>
          )}
          {collapsed && (
            <NavLink
              to="/dealer-program"
              onClick={closeSidebar}
              aria-label={`View ${tierLabel} tier and dealer program benefits`}
              title={`${tierLabel} tier benefits`}
              className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B08D55]"
            >
              <img
                src={getTierBadge(visualTierLevel)}
                alt={`${tierLabel} tier`}
                className="w-10 h-10 object-contain drop-shadow-sm"
              />
            </NavLink>
          )}

          {/* Desktop Toggle Button */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            aria-label={isCollapsed ? "Expand sidebar menu" : "Collapse sidebar menu"}
            aria-expanded={!isCollapsed}
            className={`hidden md:flex min-h-11 min-w-11 items-center justify-center p-1 absolute rounded-lg text-gray-500 hover:text-[#8A6935] hover:bg-[#B08D55]/10 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B08D55] ${isCollapsed
              ? "bottom-1 left-1/2 -translate-x-1/2"
              : "top-1/2 -translate-y-1/2 right-2"
              }`}
          >
            {isCollapsed ? <FiChevronsRight size={16} /> : <FiChevronsLeft size={16} />}
          </button>

          {/* Mobile Close Button */}
          <button
            onClick={closeSidebar}
            aria-label="Close navigation sidebar"
            className="md:hidden min-h-11 min-w-11 flex items-center justify-center absolute top-2 right-2 text-gray-500 hover:text-red-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B08D55] rounded-lg"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Navigation */}
        <nav id="dealer-sidebar-navigation" aria-label="Dealer portal navigation" className="flex-1 p-4 space-y-1 overflow-y-auto overflow-x-hidden">
          {/* Shop Now CTA */}
          <a
            href="https://framemytv.com/collections/all-deco-frames"
            target="_blank"
            rel="noopener noreferrer"
            title={collapsed ? "Shop Now" : ""}
            aria-label="Shop Now, opens the Deco Frames Collection in a new tab"
            className={`dealer-shop-cta relative flex min-h-12 items-center rounded-xl shadow-sm transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B08D55] focus-visible:ring-offset-2 mb-4 group
              ${collapsed ? "justify-center p-3" : "px-4 py-3 gap-3"}`}
          >
            {/* Icon */}
            <span className="text-lg flex-shrink-0 text-white drop-shadow-sm">
              <FiShoppingCart />
            </span>

            {!collapsed && (
              <div className="flex flex-col leading-[1.1] flex-1">
                <span className="text-white font-black text-sm tracking-wide">Shop Now</span>
                <span className="text-white text-[10px] font-semibold tracking-wider uppercase whitespace-nowrap">Deco Frames Collection</span>
              </div>
            )}
          </a>

          {!collapsed && (
            <div className="dealer-nav-section" aria-hidden="true">
              <span />
              <p>Portal</p>
              <span />
            </div>
          )}

          {/* Dashboard */}
          <NavLink
            key={dashboardItem.path}
            to={dashboardItem.path}
            className={({ isActive }) =>
              `dealer-nav-item flex min-h-11 items-center gap-3 py-2 rounded-xl transition-colors duration-200 group relative font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#B08D55] ${isActive
                ? "bg-[#B08D55]/10 text-[#8A6935]"
                : "text-gray-500 hover:bg-gray-50 hover:text-[#8A6935]"
              } ${collapsed ? "justify-center px-0" : "px-4"}`
            }
            title={collapsed ? dashboardItem.name : ""}
            aria-label={collapsed ? dashboardItem.name : undefined}
            onClick={closeSidebar}
          >
            <span className="dealer-nav-icon text-lg flex-shrink-0">{dashboardItem.icon}</span>
            {!collapsed && (
              <span className="whitespace-nowrap transition-opacity duration-300 opacity-100">
                {dashboardItem.name}
              </span>
            )}
          </NavLink>

          {/* My Account Parent Item */}
          <div className="relative">
            <button
              onClick={() => {
                setIsMyAccountOpen(open => isMyAccountSubActive ? true : !open);
                if (collapsed) {
                  setIsCollapsed(false);
                }
              }}
              aria-expanded={isMyAccountOpen}
              aria-controls="dealer-account-submenu"
              aria-label={collapsed ? "My Account links" : undefined}
              data-active={isMyAccountSubActive}
              className={`dealer-nav-item w-full min-h-11 flex items-center justify-between py-2 rounded-xl transition-colors duration-200 group font-medium text-left cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#B08D55] ${isMyAccountSubActive
                ? "bg-[#B08D55]/5 text-[#8A6935]"
                : "text-gray-600 hover:bg-gray-50 hover:text-[#8A6935]"
                } ${collapsed ? "justify-center px-0" : "px-4"}`}
              title={collapsed ? "My Account" : ""}
            >
              <div className="flex items-center gap-3">
                <span className="dealer-nav-icon text-lg flex-shrink-0"><FiUser /></span>
                {!collapsed && (
                  <div className="flex flex-col items-start leading-[1.2]">
                    <span className="whitespace-nowrap font-semibold">My Account</span>
                    {storeCreditBalance && (
                      <span className="text-[11px] text-gray-500 font-medium mt-0.5 whitespace-nowrap group-hover:text-gray-600 transition-colors flex items-center gap-1.5">
                        <span>Store Credit: <span className="font-bold text-[#8A6935]">{storeCreditBalance}</span></span>
                        {storeCreditAmount > 0 && (
                          <div className="relative ml-1 flex items-center justify-center" aria-hidden="true">
                            <div className="absolute inset-0 rounded-full bg-amber-400 animate-ping opacity-60" style={{ animationDuration: '1.5s' }}></div>
                            <FaCoins className="relative z-10 text-[#eab308] drop-shadow-sm animate-bounce" size={14} style={{ animationDuration: '2s' }} />
                          </div>
                        )}
                      </span>
                    )}
                  </div>
                )}
              </div>
              {!collapsed && (
                <span className="text-sm text-gray-500 group-hover:text-[#8A6935] ml-auto">
                  {isMyAccountOpen ? <FiChevronUp /> : <FiChevronDown />}
                </span>
              )}
            </button>

            {/* My Account Submenu Items */}
            <div id="dealer-account-submenu" hidden={!isMyAccountOpen} className={`dealer-subnav mt-1 space-y-1 transition-all duration-300 ${!collapsed ? "ml-4 border-l border-[#E6DDCF] pl-2" : ""}`}>
                {myAccountSubItems.map((subItem) => (
                  <NavLink
                    key={subItem.path}
                    to={subItem.path}
                    onClick={closeSidebar}
                    className={({ isActive }) =>
                      `dealer-subnav-item flex min-h-11 items-center gap-3 py-2 rounded-lg transition-colors duration-200 group relative font-medium text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#B08D55] ${isActive
                        ? "bg-[#B08D55]/10 text-[#8A6935]"
                        : "text-gray-500 hover:bg-gray-50 hover:text-[#8A6935]"
                      } ${collapsed ? "justify-center px-0" : "px-4"}`
                    }
                    title={collapsed ? subItem.name : ""}
                    aria-label={collapsed ? subItem.name : undefined}
                  >
                    <span className="dealer-subnav-icon text-base flex-shrink-0">{subItem.icon}</span>
                    {!collapsed && (
                      <span className="whitespace-nowrap text-xs">{subItem.name}</span>
                    )}
                  </NavLink>
                ))}
            </div>
          </div>

          {/* Store Listing Parent Item */}
          <div className="relative">
            <NavLink
              to="/coming-soon-locator"
              onClick={closeSidebar}
              aria-label={collapsed ? "Store Listing, coming soon" : undefined}
              className={({ isActive }) =>
                `dealer-nav-item w-full min-h-11 flex items-center justify-between py-2 rounded-xl transition-colors duration-200 group font-medium text-left cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#B08D55] ${isActive
                  ? "bg-[#B08D55]/10 text-[#8A6935]"
                  : "text-gray-500 hover:bg-gray-50 hover:text-[#8A6935]"
                } ${collapsed ? "justify-center px-0" : "px-4"}`
              }
              title={collapsed ? "Store Listing" : ""}
            >
              <div className="flex items-center gap-4">
                <span className="dealer-nav-icon text-lg flex-shrink-0"><FiMapPin /></span>
                {!collapsed && (
                  <div className="flex flex-col items-start leading-tight">
                    <span className="whitespace-nowrap font-semibold">Store Listing</span>
                    <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded bg-red-50 border border-red-200/60">
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-60" />
                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500" />
                      </span>
                      <span className="text-[8px] font-bold text-red-500 uppercase tracking-widest">Coming Soon</span>
                    </span>
                  </div>
                )}
              </div>
            </NavLink>
          </div>

          {!collapsed && (
            <div className="dealer-nav-section dealer-nav-section--spaced" aria-hidden="true">
              <span />
              <p>Grow your business</p>
              <span />
            </div>
          )}

          {/* Separate Individual Items */}
          {separateItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={closeSidebar}
              className={({ isActive }) =>
                `dealer-nav-item flex min-h-11 items-center gap-3 py-2 rounded-xl transition-colors duration-200 group relative font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#B08D55] ${isActive
                  ? "bg-[#B08D55]/10 text-[#8A6935]"
                  : "text-gray-500 hover:bg-gray-50 hover:text-[#8A6935]"
                } ${collapsed ? "justify-center px-0" : "px-4"}`
              }
              title={collapsed ? item.name : ""}
              aria-label={collapsed ? item.name : undefined}
            >
              <span className="dealer-nav-icon text-lg flex-shrink-0">{item.icon}</span>
              {!collapsed && (
                <span className="whitespace-nowrap transition-opacity duration-300 opacity-100">
                  {item.name}
                </span>
              )}
            </NavLink>
          ))}

          {/* Divider */}
          <div className="dealer-nav-separator" aria-hidden="true" />

          {/* User Manual */}
          <NavLink
            to="/manual"
            onClick={closeSidebar}
            className={({ isActive }) =>
              `dealer-nav-item flex min-h-11 items-center gap-3 py-2 rounded-xl transition-colors duration-200 group relative font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#B08D55] ${isActive
                ? "bg-[#B08D55]/10 text-[#8A6935]"
                : "text-gray-500 hover:bg-gray-50 hover:text-[#8A6935]"
              } ${collapsed ? "justify-center px-0" : "px-4"}`
            }
            title={collapsed ? "User Manual" : ""}
            aria-label={collapsed ? "User Manual" : undefined}
          >
            <span className="dealer-nav-icon text-lg flex-shrink-0"><FiBookOpen /></span>
            {!collapsed && (
              <span className="whitespace-nowrap transition-opacity duration-300 opacity-100 font-semibold">
                User Manual
              </span>
            )}
          </NavLink>
        </nav>

        {/* Footer / User Info */}
        <div className={`dealer-sidebar-footer border-t border-gray-100 transition-all ${collapsed ? "px-2 flex justify-center" : ""}`}>
          <div className={`dealer-profile-chip flex items-center gap-3 ${collapsed ? "justify-center" : ""}`}>
            <div className="dealer-profile-avatar w-10 h-10 rounded-full bg-gradient-to-br from-[#B08D55] to-[#967645] text-white flex items-center justify-center font-bold text-sm shadow-md shadow-[#B08D55]/20 flex-shrink-0">
              {user?.firstName?.charAt(0) || user?.companyName?.charAt(0) || "U"}
            </div>
            {!collapsed && (
              <div className="flex-1 overflow-hidden text-left">
                <p className="text-sm font-semibold text-gray-800 truncate">
                  {user?.firstName || user?.lastName
                    ? `${user?.firstName || ""} ${user?.lastName || ""}`.trim()
                    : user?.companyName || "Your Account"}
                </p>
                <p className="text-xs font-bold text-gray-500 truncate">
                  {user?.companyName}
                </p>
              </div>
            )}
          </div>
          <div className="dealer-version-wrap mt-3">
            <VersionBadge onClick={() => setIsReleaseNotesOpen(true)} />
          </div>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main inert={isSidebarOpen} className="flex-1 h-full overflow-y-auto relative">
        <div className="mx-auto w-full px-3 md:px-5 py-8">
          {/* Mobile Toggle Button */}
          <div className="md:hidden mb-6 bg-gray-50/95 backdrop-blur-sm border-b border-gray-200/50">
            <button
              ref={mobileMenuButtonRef}
              onClick={() => setIsSidebarOpen(true)}
              aria-label="Open navigation sidebar menu"
              aria-expanded={isSidebarOpen}
              aria-controls="dealer-sidebar"
              className="min-h-11 min-w-11 p-2 text-gray-600 hover:text-[#B08D55] transition-colors rounded-lg hover:bg-white/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B08D55]"
            >
              <FiMenu size={24} />
            </button>
          </div>

          <Outlet />
        </div>
      </main>
      <EditProfileModal />
      <PlanSelectionModal />
      <SubscriptionDetailsModal />
      <ReleaseNotesModal isOpen={isReleaseNotesOpen} onClose={() => setIsReleaseNotesOpen(false)} />
      {/* <TierPreviewSwitcher /> */}
    </div>
  );
}
